import { cn } from '@/lib/cn';
import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

/**
 * JEDYNY przycisk aplikacji (redesign 2026-09-30). Zastepuje ~20 recznie
 * skladanych wariantow „przycisku glownego” i ~22 drugorzednych.
 *
 * Hierarchia na ekranie: max 1 `primary`, reszta `secondary`/`ghost`,
 * akcje nieodwracalne `danger` (najlepiej w menu „Wiecej”).
 * Telefon: min. 44 px wysokosci (touch target), od `md` kompaktowo.
 */
export const buttonVariants = cva(
  'inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-150 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-fg shadow-sm hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-accent-ring',
        secondary:
          'border border-border bg-surface text-fg shadow-sm hover:border-border-strong hover:bg-surface-2',
        ghost: 'text-fg-muted hover:bg-surface-2 hover:text-fg',
        danger:
          'border border-error/30 bg-error-subtle text-error hover:border-error/50 hover:bg-error/15',
        success:
          'border border-success/30 bg-success-subtle text-success hover:border-success/50 hover:bg-success/15',
      },
      size: {
        sm: 'min-h-touch px-3 text-sm md:min-h-8 md:px-2.5 md:text-xs [&_svg]:h-3.5 [&_svg]:w-3.5',
        md: 'min-h-touch px-4 text-sm md:min-h-9 md:px-3.5 [&_svg]:h-4 [&_svg]:w-4',
        lg: 'min-h-12 px-5 text-base md:min-h-11 [&_svg]:h-[18px] [&_svg]:w-[18px]',
        icon: 'h-touch w-touch md:h-9 md:w-9 [&_svg]:h-[18px] [&_svg]:w-[18px]',
        'icon-sm': 'h-touch w-touch md:h-8 md:w-8 [&_svg]:h-4 [&_svg]:w-4',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'secondary', size: 'md', block: false },
  },
);

type Variants = VariantProps<typeof buttonVariants>;

interface CommonProps extends Variants {
  icon?: LucideIcon;
  /** Ikona po prawej (np. strzalka „dalej”). */
  iconRight?: LucideIcon;
  loading?: boolean;
  children?: ReactNode;
  className?: string;
}

export type ButtonProps = CommonProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

function Content({ icon: Icon, iconRight: IconRight, loading, children }: CommonProps) {
  return (
    <>
      {loading ? (
        <Loader2 className="animate-spin" strokeWidth={2} />
      ) : (
        Icon && <Icon strokeWidth={2} />
      )}
      {children}
      {IconRight && !loading && <IconRight strokeWidth={2} />}
    </>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant,
    size,
    block,
    icon,
    iconRight,
    loading,
    children,
    className,
    type = 'button',
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, block }), className)}
      {...rest}
    >
      <Content icon={icon} iconRight={iconRight} loading={loading}>
        {children}
      </Content>
    </button>
  );
});

/** Link wygladajacy jak przycisk (nawigacja wewnatrz aplikacji). */
export function ButtonLink({
  variant,
  size,
  block,
  icon,
  iconRight,
  children,
  className,
  ...rest
}: CommonProps & Omit<LinkProps, 'children' | 'className'>) {
  return (
    <Link className={cn(buttonVariants({ variant, size, block }), className)} {...rest}>
      <Content icon={icon} iconRight={iconRight}>
        {children}
      </Content>
    </Link>
  );
}
